import { Injectable, signal, effect } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Observable, catchError, throwError } from 'rxjs';
import {
  CreateReservationForUserRequest,
  CreateReservationForGuestRequest,
  Reservation,
  UpdateReservationRequest,
  UpdateReservationGroupRequest
} from '../models/sala.model';
import { environment } from '../../environments/environment';
import { AuthService } from './auth.service';
import { friendlyError } from '../utils/friendly-error';

@Injectable({
  providedIn: 'root'
})
export class ReservationService {
  // El endpoint base es /v1/reservations/admin
  private readonly API_URL = `${environment.apiUrl}/reservations/admin`;

  // Señal reactiva para las reservaciones
  public readonly reservaciones = signal<Reservation[]>([]);
  public readonly totalReservaciones = signal(0);

  private lastBusinessId: number | null | undefined = undefined;

  constructor(private http: HttpClient, private auth: AuthService) {
    // Multi-tenant: evita que las reservas de un negocio queden visibles al
    // cambiar de cuenta en la misma pestaña (ver SalaService.reset).
    effect(() => {
      const businessId = this.auth.currentUser()?.id ?? null;
      if (this.lastBusinessId !== undefined && businessId !== this.lastBusinessId) {
        this.reset();
      }
      this.lastBusinessId = businessId;
    });
  }

  /** Limpia el estado en memoria al cambiar de sesión/negocio. */
  reset(): void {
    this.reservaciones.set([]);
    this.totalReservaciones.set(0);
  }

  verificarDisponibilidad(
    spaceId: number,
    reservationDate: string,
    startTime: string,
    endTime: string,
    numberOfPeople: number
  ): Observable<{ available: boolean; message?: string; suggestion?: string }> {
    const params = new HttpParams()
      .set('entryDate', reservationDate)
      .set('startTime', startTime)
      .set('endTime', endTime)
      .set('numberOfPeople', numberOfPeople);

    return this.http.get<{ available: boolean; message?: string; suggestion?: string }>(
      `${environment.apiUrl}/reservations/check-availability/${spaceId}`,
      { params }
    ).pipe(catchError(this.handleError));
  }

  /**
   * Días con reservas ya existentes, hacia adelante Y hacia atrás — ruta de
   * ADMIN (no la pública que usa el cliente para su propio calendario de
   * reserva, PropertyService.getOccupiedSlots en elhubsvoffice_front). Antes
   * el panel usaba esa misma ruta pública, que a propósito solo cubre desde
   * hoy en adelante (un cliente nunca reserva hacia atrás) -- el panel sí
   * necesita ver las fechas YA reservadas para tener el historial de
   * ocupación identificado al elegir una fecha nueva, así que se separó en
   * su propio endpoint en vez de tocar el público.
   */
  obtenerHorariosOcupados(spaceId: number, days = 3660, pastDays = 365): Observable<{
    occupied_days: Array<{ date: string; slots: Array<{ start: string; end: string }> }>;
    /** Días/horarios no disponibles (asueto, mantenimiento...) de hoy en adelante. */
    blocked_days?: Array<{ date: string; all_day: boolean; slots: Array<{ start: string; end: string }>; reason_label: string }>;
  }> {
    const params = new HttpParams()
      .set('days', Math.min(3660, Math.max(1, days)).toString())
      .set('past_days', Math.min(3660, Math.max(0, pastDays)).toString());
    return this.http.get<{
      occupied_days: Array<{ date: string; slots: Array<{ start: string; end: string }> }>;
      blocked_days?: Array<{ date: string; all_day: boolean; slots: Array<{ start: string; end: string }>; reason_label: string }>;
    }>(
      `${environment.apiUrl}/reservations/admin/occupied-slots/${spaceId}`,
      { params }
    ).pipe(catchError(this.handleError));
  }

  /**
   * Crear reservación para un usuario registrado
   */
  crearReservacionParaUsuario(request: CreateReservationForUserRequest): Observable<Reservation> {
    return this.http.post<Reservation>(`${this.API_URL}/create-for-user`, request).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Crear reservación para un invitado
   */
  crearReservacionParaInvitado(request: CreateReservationForGuestRequest): Observable<Reservation> {
    return this.http.post<Reservation>(`${this.API_URL}/create-guest`, request).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Listar todas las reservaciones (si hay endpoint)
   */
  listarReservaciones(): Observable<Reservation[]> {
    // Ajustar la URL según el endpoint real de listado
    return this.http.get<Reservation[]>(`${this.API_URL}`).pipe(
      catchError(this.handleError)
    );
  }

  getAdminReservations(filters: {
    page?: number;
    limit?: number;
    status?: string;
    payment_status?: string;
    search?: string;
    month?: number;
    year?: number;
    space_id?: number;
  }): Observable<{ count: number; reservations: Reservation[]; pages: number; page: number; limit: number }> {
    let params = new HttpParams()
      .set('page', String(filters.page || 1))
      .set('limit', String(filters.limit || 25));
    if (filters.status) params = params.set('status', filters.status);
    if (filters.payment_status) params = params.set('payment_status', filters.payment_status);
    if (filters.search) params = params.set('search', filters.search.trim());
    if (filters.month) params = params.set('month', String(filters.month));
    if (filters.year) params = params.set('year', String(filters.year));
    if (filters.space_id) params = params.set('space_id', String(filters.space_id));

    return this.http.get<{ count: number; reservations: Reservation[]; pages: number; page: number; limit: number }>(
      `${this.API_URL}`,
      { params }
    ).pipe(catchError(this.handleError));
  }

  loadReservations(): void {
    this.http.get<{ count: number; reservations: Reservation[] }>(`${this.API_URL}`).subscribe({
      next: (response) => {
        console.log('[ReservationService] loadReservations response:', response);
        this.reservaciones.set(response.reservations || []);
        this.totalReservaciones.set(Number(response.count || 0));
      },
      error: (err) => {
        console.error('[ReservationService] loadReservations error:', err);
        this.reservaciones.set([]);
        this.totalReservaciones.set(0);
      },
    });
  }

  actualizarReservacion(id: number, request: UpdateReservationRequest): Observable<{ msg: string; reservation: Reservation }> {
    return this.http.put<{ msg: string; reservation: Reservation }>(`${this.API_URL}/update/${id}`, request).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Sube un comprobante de pago externo (imagen o PDF) para una reserva
   * marcada como "Pagada". Devuelve el file_name que luego se envía en el
   * payload de crear/editar la reserva dentro de `receipts`.
   */
  subirComprobante(archivo: File): Observable<{ file_name: string; public_url: string; original_name: string; file_type: string; mime: string }> {
    const formData = new FormData();
    formData.append('comprobante', archivo, archivo.name);
    return this.http.post<{ file_name: string; public_url: string; original_name: string; file_type: string; mime: string }>(
      `${this.API_URL}/receipt/upload`,
      formData
    ).pipe(catchError(this.handleError));
  }

  eliminarReservacion(id: number, reason: string, notifyCustomer = false): Observable<{ msg: string; scheduled_deletion_at: string }> {
    return this.http.delete<{ msg: string; scheduled_deletion_at: string }>(`${this.API_URL}/delete/${id}`, {
      body: { reason, notify_customer: notifyCustomer }
    }).pipe(catchError(this.handleError));
  }

  /**
   * Aplica el mismo cambio a TODAS las fechas activas de una reserva
   * multi-día de una sola vez (ver updateReservationGroupByAdmin). Si el
   * conjunto ya estaba pagado y el cambio sube el precio, el backend genera
   * un enlace de pago solo por la diferencia (payment_difference_link_url).
   */
  actualizarReservacionGrupo(groupId: string, request: UpdateReservationGroupRequest): Observable<{
    msg: string;
    affected_reservation_ids: number[];
    payment_difference_amount: number | null;
    payment_difference_link_url: string | null;
  }> {
    return this.http.put<{
      msg: string;
      affected_reservation_ids: number[];
      payment_difference_amount: number | null;
      payment_difference_link_url: string | null;
    }>(`${this.API_URL}/group/${groupId}/update`, request).pipe(catchError(this.handleError));
  }

  /** Envía a la Papelera TODAS las fechas activas de un conjunto de una sola vez. */
  eliminarReservacionGrupo(groupId: string, reason: string, notifyCustomer = false): Observable<{ msg: string; scheduled_deletion_at: string; affected_reservation_ids: number[] }> {
    return this.http.delete<{ msg: string; scheduled_deletion_at: string; affected_reservation_ids: number[] }>(`${this.API_URL}/group/${groupId}/delete`, {
      body: { reason, notify_customer: notifyCustomer }
    }).pipe(catchError(this.handleError));
  }

  /**
   * Reenvía (o genera, si venció) el enlace público de pago de una reserva
   * pendiente. Si la reserva ya se pagó mientras tanto (p. ej. el cliente
   * pagó justo antes de que el admin presionara el botón), el backend NO
   * reenvía nada: responde `status:'already_paid'` con el enlace para que
   * el admin pueda abrir la vista pública y confirmar el resultado.
   *
   * scope='individual' ata el enlace SOLO a esta reservation_id, aunque
   * pertenezca a un conjunto -- se usa desde la fila individual (dentro del
   * modal de fechas) para no cobrar de más por el resto del grupo. Sin
   * scope (o 'group'), el comportamiento es el de siempre: si la reserva
   * pertenece a un conjunto, el enlace cubre todas las fechas que sigan sin
   * pagar -- se usa desde la tarjeta de grupo.
   */
  // `notify=false` (usado para "Copiar enlace") crea/resincroniza el enlace
  // igual que siempre y lo devuelve, pero SIN disparar el correo -- para
  // cuando el admin solo quiere el enlace a mano (WhatsApp u otra red), sin
  // reenviar el correo de nuevo.
  resendPaymentLink(id: number, scope: 'individual' | 'group' = 'group', notify = true): Observable<{ status: 'sent' | 'already_paid'; msg: string; paymentLinkUrl: string | null }> {
    let params = new HttpParams();
    if (scope === 'individual') params = params.set('scope', 'individual');
    if (!notify) params = params.set('notify', 'false');
    return this.http.post<{ status: 'sent' | 'already_paid'; msg: string; paymentLinkUrl: string | null }>(
      `${this.API_URL}/${id}/resend-payment-link`, {}, { params }
    ).pipe(catchError(this.handleError));
  }

  /**
   * Detalle paginado de un conjunto -- consulta propia, separada de la
   * lista general (getAdminReservations ya no trae todas las fechas de
   * cada grupo, solo el resumen). Se dispara al abrir el modal "ver fechas
   * del conjunto", nunca antes.
   *
   * `q` es un único campo de búsqueda que el backend interpreta como
   * número/referencia de reserva, día del mes o nombre/número de mes -- ver
   * getReservationGroupDates.
   */
  getReservationGroupDates(groupId: string, filters: {
    page?: number;
    limit?: number;
    q?: string;
  }): Observable<{ count: number; reservations: Reservation[]; pages: number; page: number; limit: number }> {
    let params = new HttpParams()
      .set('page', String(filters.page || 1))
      .set('limit', String(filters.limit || 25));
    if (filters.q) params = params.set('q', filters.q.trim());

    return this.http.get<{ count: number; reservations: Reservation[]; pages: number; page: number; limit: number }>(
      `${this.API_URL}/group/${groupId}/dates`,
      { params }
    ).pipe(catchError(this.handleError));
  }

  private handleError(error: HttpErrorResponse): Observable<never> {
    const errorMessage = friendlyError(error, 'Ocurrió un error. Intenta nuevamente.');
    console.error('Error HTTP:', error);
    return throwError(() => errorMessage);
  }
}
